import { useSnackbar } from 'notistack';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  getSiteTaskDetail,
  getSiteTaskList,
  updateSiteTask
} from '../../../../apicalls/task';
import { getSiteProcessNameList } from '../../../../apicalls/process';
import { TSiteProcessNameListData } from '../../../../types/process';
import { checkResData } from '../../../../utils/common';
import { TSelectItem } from '../../../../types/common';
import { TSiteTaskDetailData, TSiteTaskListData } from '../../../../types/task';
import { SITE_TASK_STATUS, SEARCH_STATUS } from '../../../../const/TaskConst';
import {
  createSiteProcessAndSiteTaskEmployeeList,
  parseNumber
} from '../../../../utils/processAndTaskFormUtils';

export const useHooks = (
  constructId: number,
  siteProcessId?: number,
  viewAlert?: boolean
) => {
  // ページネーション用
  const [displayLimit, setDisplayLimit] = useState<number>(10);
  // 現在のページ
  const [currentPage, setCurrentPage] = useState(1);
  // 選択中の工程
  const [searchSiteProcessId, setSearchSiteProcessId] =
    useState<number | undefined>(undefined);
  // 選択中の担当者
  const [searchUserId, setSearchUserId] = useState(0);
  // 選択中のステータス
  const [searchStatus, setSearchStatus] = useState(SEARCH_STATUS.ALL.value);
  // 総ページ数
  const [pageCount, setPageCount] = useState(1);
  // 工程選択リスト
  const [siteProcessList, setSiteProcessList] = useState<TSelectItem[]>([
    { value: 0, label: '工程' }
  ]);
  // 担当者リスト
  const [userList, setUserList] = useState<TSelectItem[]>([
    { value: 0, label: '担当者' }
  ]);
  // タスク一覧データ
  const [siteTaskList, setSiteTaskList] = useState<Array<TSiteTaskListData>>(
    []
  );
  // 変更履歴表示関連
  const [isOpenSiteTaskHistory, setIsOpenSiteTaskHistory] = useState(false);
  // タスク追加モーダル表示用
  const [isOpenSiteTaskAdd, setIsOpenSiteTaskAdd] = useState(false);
  // タスク更新モーダル表示用
  const [isOpenSiteTaskEdit, setIsOpenSiteTaskEdit] = useState(false);
  // タスク削除モーダル表示用
  const [isOpenSiteTaskDelete, setIsOpenSiteTaskDelete] = useState(false);
  // 更新対象のタスク特定用
  const [editSiteTaskId, setEditSiteTaskId] = useState(0);
  // 削除対象のタスク特定用
  const [deleteSiteTaskId, setDeleteSiteTaskId] = useState(0);
  // 再レンダリングのトリガ用state
  const [renderSiteTaskDataTrigger, setRenderSiteTaskDataTrigger] =
    useState(false);
  // 再レンダリング
  const reRenderSiteTaskData = () => {
    setRenderSiteTaskDataTrigger(prev => !prev);
  };

  // ローディング表示制御
  const currentConstructId = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMovingPage, setIsMovingPage] = useState(false);
  const isAllowedSearching = useRef(false);

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // タスク一覧情報の取得
  useEffect(() => {
    if (!isAllowedSearching.current) return;
    if (constructId > 0) {
      if (currentConstructId.current !== constructId) {
        currentConstructId.current = constructId;
        setIsLoading(true);
      } else {
        setIsMovingPage(true);
      }

      const taskStatus = getTaskStatusForAPI(searchStatus);
      const alertOnly = searchStatus === SEARCH_STATUS.ALERT_ONLY.value;
      getSiteTaskList(
        constructId,
        searchSiteProcessId,
        searchUserId,
        taskStatus,
        alertOnly,
        false,
        currentPage,
        displayLimit
      ).then(resData => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            setSiteTaskList(data.data);
            setPageCount(data.page_count);
            window.scrollTo(0, 0);
          }
          setIsLoading(false);
          setIsMovingPage(false);
        });
      });
    }
  }, [
    currentPage,
    displayLimit,
    searchStatus,
    searchSiteProcessId,
    searchUserId,
    renderSiteTaskDataTrigger
  ]);

  // 初期値設定
  useEffect(() => {
    // 工程一覧セレクトボックスの設定
    getSiteProcessNameList(constructId, false).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const listData: TSiteProcessNameListData[] = data.data;
          const list: TSelectItem[] = [];
          list.push({ value: 0, label: '工程' });
          listData.forEach((siteProcess: TSiteProcessNameListData) => {
            list.push({
              value: siteProcess.id,
              label:
                siteProcess.parent_site_process_id === null
                  ? siteProcess.site_process_name
                  : '　' + siteProcess.site_process_name
            });
          });
          setSiteProcessList(list);
        }
      });
    });

    // 担当者選択プルダウン
    createSiteProcessAndSiteTaskEmployeeList(
      constructId,
      enqueueSnackbar,
      '担当者'
    ).then(employeeList => {
      setUserList(employeeList);
    });

    // 工程IDが指定されている場合、工程IDをセット
    if (siteProcessId !== null && siteProcessId !== undefined) {
      setSearchSiteProcessId(siteProcessId);
    }

    // アラートのみ表示の場合、検索ステータスをセット
    if (viewAlert === true) {
      setSearchStatus(SEARCH_STATUS.ALERT_ONLY.value);
    }

    isAllowedSearching.current = true;
  }, []);

  // 工程セレクトボックス選択時処理
  const handleChangeSearchSiteProcess = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchSiteProcessId(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // 担当者セレクトボックス選択時処理
  const handleChangeSearchUser = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchUserId(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // ステータスセレクトボックス選択時処理
  const handleChangeSearchStatus = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchStatus(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // 表示件数セレクトボックス選択時処理
  const handleChangeDisplayLimit = (e: ChangeEvent<HTMLSelectElement>) => {
    setDisplayLimit(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // 変更履歴ボタン押下時処理
  const handleOnClickHistory = () => {
    setIsOpenSiteTaskHistory(true);
  };

  // ステータス変更時の処理
  const handleChangeStatus = (
    e: ChangeEvent<HTMLSelectElement>,
    taskId: number
  ) => {
    const status = parseNumber(e.target.value) ?? 0;
    updateStatus(taskId, status);
  };

  // タスクのステータスを更新する
  const updateStatus = (taskId: number, status: number) => {
    getSiteTaskDetail(taskId).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const siteTask: TSiteTaskDetailData = data.data;
          updateSiteTask(
            siteTask.id,
            siteTask.site_process.id,
            siteTask.site_task_name,
            status,
            siteTask.charge_user?.id,
            siteTask.planned_end_date,
            siteTask.detail,
            siteTask.order_category,
            siteTask.order?.id,
            siteTask.actual_end_date
          ).then((resData: any) => {
            checkResData(resData, enqueueSnackbar).then(() => {
              reRenderSiteTaskData();
            });
          });
        }
      });
    });
  };

  // タスク一覧取得API用タスクステータス取得処理
  const getTaskStatusForAPI = (searchStatus: number) => {
    // 「すべて」と「アラートのみ」の場合はステータス指定なし
    if (searchStatus === SEARCH_STATUS.ALL.value) return undefined;
    if (searchStatus === SEARCH_STATUS.ALERT_ONLY.value) return undefined;
    // 「すべて」と「アラートのみ」以外は検索用ステータスとタスクステータスは一致
    if (searchStatus === SEARCH_STATUS.NOT_STARTED.value)
      return SITE_TASK_STATUS.NOT_STARTED.value;
    if (searchStatus === SEARCH_STATUS.IN_PROGRESS.value)
      return SITE_TASK_STATUS.IN_PROGRESS.value;
    if (searchStatus === SEARCH_STATUS.COMPLETED.value)
      return SITE_TASK_STATUS.COMPLETED.value;
    if (searchStatus === SEARCH_STATUS.UNNECESSARY.value)
      return SITE_TASK_STATUS.UNNECESSARY.value;
    return searchStatus;
  };

  return {
    displayLimit,
    pageCount,
    currentPage,
    siteTaskList,
    siteProcessList,
    userList,
    searchSiteProcessId,
    searchUserId,
    searchStatus,
    isOpenSiteTaskAdd,
    isOpenSiteTaskEdit,
    isOpenSiteTaskDelete,
    isOpenSiteTaskHistory,
    editSiteTaskId,
    deleteSiteTaskId,
    setCurrentPage,
    setIsOpenSiteTaskAdd,
    setIsOpenSiteTaskEdit,
    setIsOpenSiteTaskDelete,
    setIsOpenSiteTaskHistory,
    setEditSiteTaskId,
    setDeleteSiteTaskId,
    handleOnClickHistory,
    handleChangeSearchSiteProcess,
    handleChangeSearchUser,
    handleChangeSearchStatus,
    handleChangeDisplayLimit,
    reRenderSiteTaskData,
    handleChangeStatus,
    isLoading,
    isMovingPage
  };
};
